import { allLifecycleEventsForPlatform } from '@/components/automations/automationHelperComponents';
import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { isReleaseSkipped } from '@/helpers';
import { RootState } from '@/reducers/reducers';
import { selectAppPlatform, selectAppTimeFormatSetting, selectCadenceTimezone } from '@/selectors/selectors';
import Text from 'antd/lib/typography/Text';
import { UpcomingReleaseSummary, CompletedReleaseSummary, NextReleaseSummary, LifecycleStep } from '@/types';
import { RetweetOutlined, CheckCircleFilled } from '@ant-design/icons';
import Divider from 'antd/lib/divider';
import * as React from 'react';
import { useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat, formatDateWithTimezone } from '@/timeHelpers';
import { capitalize } from 'lodash';
import { useReleaseLifecycleStepDisplayInfo } from '@/utils/hooks/helpers';

export interface TargetDatesProps {
    appId: string;
    summary: UpcomingReleaseSummary | CompletedReleaseSummary | NextReleaseSummary;
}

export const TargetDatesWidget: React.FC<TargetDatesProps> = ({ appId, summary }) => {
    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, appId));
    const cadenceTimezone = useSelector((state: RootState) => selectCadenceTimezone(state, appId));
    const lifecycleSteps = allLifecycleEventsForPlatform(appPlatform);

    const dateFormat = DatetimeFormat.ShortDayMonthDate;
    const timeFormat = (targetDate: dayjs.Dayjs) => {
        const format = targetDate.minute() !== 0 ? 'shortHourOnly' : 'hourOnly';
        return formatDateWithTimezone(targetDate, format, cadenceTimezone, use24HourTimeFormat);
    };

    let skippedAtDate = null;
    let skippedAtTime = null;
    if (summary && 'status' in summary && isReleaseSkipped(summary.status)) {
        skippedAtDate = summary.skippedAt ? dayjs(summary.skippedAt).format(dateFormat) : null;
        skippedAtTime = summary.skippedAt ? timeFormat(dayjs(summary.skippedAt)) : null;
    }

    let releasedAtDate = null;
    let releasedAtTime = null;
    if (summary && 'completedAt' in summary && summary.completedAt) {
        releasedAtDate = summary.completedAt ? dayjs(summary.completedAt).format(dateFormat) : null;
        releasedAtTime = summary.completedAt ? timeFormat(dayjs(summary.completedAt)) : null;
    }

    let phasedReleaseCompletedAtDate = null;
    let phasedReleaseCompletedAtTime = null;
    if (summary && 'phasedRelease' in summary && summary.phasedRelease && summary.phasedRelease.completedAt) {
        const completedAt = dayjs(summary.phasedRelease.completedAt);
        phasedReleaseCompletedAtDate = dayjs(summary.phasedRelease.completedAt).format(dateFormat);
        phasedReleaseCompletedAtTime = timeFormat(completedAt);
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                borderColor: antGrey.grey4,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 8,
                backgroundColor: antGrey.grey3,
                padding: '12px 20px',
                marginBottom: 26,
            }}
        >
            {skippedAtDate ?
                <MiniDateBlockBase
                    displayDate={skippedAtDate}
                    displayTime={skippedAtTime}
                    title={'Skipped'}
                    automationIcon={false}
                    isComplete
                    hasDate
                    shouldBeBolded
                    showCadenceIcon={false}
                />
            : releasedAtDate ?
                <>
                    <MiniDateBlockBase
                        displayDate={releasedAtDate}
                        displayTime={releasedAtTime}
                        title={'Released'}
                        automationIcon={false}
                        isComplete
                        hasDate
                        shouldBeBolded
                        showCadenceIcon={false}
                    />

                    {phasedReleaseCompletedAtDate && (
                        <>
                            <Divider style={{ width: '100%', margin: '10px 0' }} />
                            <MiniDateBlockBase
                                displayDate={phasedReleaseCompletedAtDate}
                                displayTime={phasedReleaseCompletedAtTime}
                                title={'Phased release 100%'}
                                automationIcon={false}
                                isComplete
                                hasDate
                                shouldBeBolded
                                showCadenceIcon={false}
                            />
                        </>
                    )}
                </>
            :   lifecycleSteps.map((step, index) => {
                    return (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column' }}>
                            {index ?
                                <Divider style={{ width: '100%', margin: '10px 0' }} />
                            :   null}
                            <MiniDateBlock step={step} appId={appId} releaseSummary={summary} />
                        </div>
                    );
                })
            }
        </div>
    );
};

const MiniDateBlock = ({
    step,
    appId,
    releaseSummary,
}: {
    releaseSummary: NextReleaseSummary | UpcomingReleaseSummary | CompletedReleaseSummary;
    step: LifecycleStep;
    appId: string;
}) => {
    const {
        automationIcon,
        showCadenceIcon,
        isComplete,
        automationEnabled,
        targetDate,
        displayText,
        hasDate,
        displayTime,
        displayDate,
        isNextRelevantStep,
    } = useReleaseLifecycleStepDisplayInfo(step, '', appId, true, releaseSummary);

    const shouldBeBolded = isNextRelevantStep;

    const title =
        !isComplete && (automationEnabled || targetDate.isValid()) ?
            capitalize('Target ' + displayText + ' Date')
        :   displayText;

    return (
        <MiniDateBlockBase
            displayDate={displayDate}
            displayTime={displayTime}
            title={title}
            automationIcon={automationIcon}
            isComplete={isComplete}
            hasDate={hasDate}
            shouldBeBolded={shouldBeBolded}
            showCadenceIcon={showCadenceIcon}
        />
    );
};

export const MiniDateBlockBase = ({
    displayDate,
    displayTime,
    automationIcon,
    isComplete,
    hasDate,
    title,
    shouldBeBolded,
    showCadenceIcon,
}: {
    displayDate: string;
    displayTime: string;
    title: string;
    automationIcon: React.ReactNode;
    isComplete: boolean;
    hasDate: boolean;
    shouldBeBolded: boolean;
    showCadenceIcon: boolean;
}) => {
    return (
        <div style={{ minWidth: '180px' }}>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingBottom: '6px',
                    gap: '10px',
                }}
            >
                {showCadenceIcon && (
                    <RetweetOutlined
                        style={{
                            fontSize: 14,
                            color: antGrey.grey6,
                        }}
                    />
                )}
                <Text style={{ ...appHomeCardsWidgetTitleStyle }}>{title}</Text>
                {automationIcon}
                {isComplete && <CheckCircleFilled style={{ fontSize: 16, color: runwayColors.statusGreen }} />}
            </div>
            <Text
                style={{
                    fontSize: '16px',
                    color: !hasDate ? antGrey.grey6 : antGrey.grey8,
                }}
            >
                <span
                    style={{
                        fontWeight: shouldBeBolded ? 600 : 400,
                    }}
                >
                    {displayDate}
                </span>
                <span
                    style={{
                        color: !hasDate ? antGrey.grey5 : antGrey.grey6,
                    }}
                >
                    {'  /  '}
                </span>
                <span
                    style={{
                        fontWeight: shouldBeBolded ? 600 : 400,
                    }}
                >
                    {displayTime}
                </span>
            </Text>
        </div>
    );
};
